/**
 * useProductValidationV3 - Schema-driven validation hook for V3 product editor
 *
 * Validates product form state against family schema.
 * Provides real-time field validation and error tracking.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import i18n from 'i18next';
import {
  ProductFormStateV3,
  UseProductValidationV3Return,
  ValidationResultV3,
  ValidationMessage,
  ValidationRule,
  ValidationContext,
  FieldPath,
  VariantV3,
  createEmptyValidationResult,
  isDimensionValue,
  SPECIAL_ATTRIBUTE_FIELDS,
} from '../types';
import type {
  ProductFamily,
  FamilyAttribute,
  VariantAxisConfig,
} from '../../../../common/models/product-family.model';
import {
  getRequiredAttributes,
  getEnabledVariantAxes,
} from '../../../../common/models/product-family.model';
import { resolveTranslation } from '../../../../common/utils/translatable-string';
import { isCabochonPattern, isUnbalancedCabochonList } from '../utils/cabochonImages';
import { isOpusImageCountValid, isOpusPattern } from '../utils/opusImages';

// ============================================================================
// Types
// ============================================================================

export interface UseProductValidationV3Options {
  /** Product form state to validate */
  product: ProductFormStateV3 | null;
  /** Product family configuration */
  family: ProductFamily;
  /** Variants to validate */
  variants?: VariantV3[];
  /** Uploaded plank images keyed by plankGroupId (for surface texture validation) */
  uploadedPlankImages?: { [plankGroupId: string]: File[] };
  /** Enable real-time validation */
  realTimeValidation?: boolean;
}

// ============================================================================
// Built-in Validation Rules
// ============================================================================

/**
 * Create required field validation rule
 */
const tv = (key: string, params?: Record<string, unknown>) =>
  i18n.t(`PAGES.NEW_PRODUCT.V3.VALIDATION.${key}`, params ?? {});

type LabelSource = string | (() => string);
const resolveLabel = (l: LabelSource) => (typeof l === 'function' ? l() : l);

function createRequiredRule(field: FieldPath, label: LabelSource): ValidationRule {
  return {
    id: `required:${field}`,
    field,
    validate: (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    },
    message: () => tv('REQUIRED', { label: resolveLabel(label) }),
    severity: 'error',
    enabled: true,
  };
}

/**
 * Create min length validation rule
 */
function createMinLengthRule(
  field: FieldPath,
  label: LabelSource,
  minLength: number
): ValidationRule {
  return {
    id: `minLength:${field}`,
    field,
    validate: (value) => {
      if (typeof value !== 'string') return true;
      return value.length >= minLength;
    },
    message: () => tv('MIN_LENGTH', { label: resolveLabel(label), min: minLength }),
    severity: 'error',
    enabled: true,
  };
}

/**
 * Create max length validation rule
 */
function createMaxLengthRule(
  field: FieldPath,
  label: LabelSource,
  maxLength: number
): ValidationRule {
  return {
    id: `maxLength:${field}`,
    field,
    validate: (value) => {
      if (typeof value !== 'string') return true;
      return value.length <= maxLength;
    },
    message: () => tv('MAX_LENGTH', { label: resolveLabel(label), max: maxLength }),
    severity: 'error',
    enabled: true,
  };
}

/**
 * Create URL validation rule
 */
function createUrlRule(field: FieldPath, label: LabelSource): ValidationRule {
  return {
    id: `url:${field}`,
    field,
    validate: (value) => {
      if (!value || typeof value !== 'string' || value.trim() === '') return true;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    message: () => tv('URL_INVALID', { label: resolveLabel(label) }),
    severity: 'error',
    enabled: true,
  };
}

/**
 * Create number range validation rule
 */
function createRangeRule(
  field: FieldPath,
  label: LabelSource,
  min?: number,
  max?: number
): ValidationRule {
  return {
    id: `range:${field}`,
    field,
    validate: (value) => {
      if (value === null || value === undefined) return true;
      const num = typeof value === 'number' ? value : Number(value);
      if (isNaN(num)) return true;
      if (min !== undefined && num < min) return false;
      if (max !== undefined && num > max) return false;
      return true;
    },
    message: () => {
      const l = resolveLabel(label);
      if (min !== undefined && max !== undefined) return tv('RANGE_BETWEEN', { label: l, min, max });
      if (min !== undefined) return tv('RANGE_MIN', { label: l, min });
      if (max !== undefined) return tv('RANGE_MAX', { label: l, max });
      return tv('PATTERN_INVALID', { label: l });
    },
    severity: 'error',
    enabled: true,
  };
}

/**
 * Create pattern validation rule
 */
function createPatternRule(
  field: FieldPath,
  label: LabelSource,
  pattern: string,
  message?: string
): ValidationRule {
  const regex = new RegExp(pattern);
  return {
    id: `pattern:${field}`,
    field,
    validate: (value) => {
      if (!value || typeof value !== 'string' || value.trim() === '') return true;
      return regex.test(value);
    },
    message: () => message || tv('PATTERN_INVALID', { label: resolveLabel(label) }),
    severity: 'error',
    enabled: true,
  };
}

// ============================================================================
// Rule Generation from Family Schema
// ============================================================================

/**
 * Generate validation rules from family configuration
 */
function generateRulesFromFamily(family: ProductFamily): ValidationRule[] {
  const rules: ValidationRule[] = [];

  // Core required fields (stored at root level, not in attributes).
  // Labels are passed as getters so they resolve against the current language
  // each time the validation message is produced.
  const nameLabel = () => i18n.t('PAGES.NEW_PRODUCT.V3.FIELDS.NAME');
  const brandLabel = () => i18n.t('PAGES.NEW_PRODUCT.V3.FIELDS.BRAND');
  const productTypeLabel = () => i18n.t('PAGES.NEW_PRODUCT.V3.FIELDS.PRODUCT_TYPE');
  const applicationTypesLabel = () => i18n.t('PAGES.NEW_PRODUCT.V3.FIELDS.APPLICATION_TYPES');

  rules.push(createRequiredRule('name', nameLabel));
  rules.push(createMinLengthRule('name', nameLabel, 2));
  rules.push(createMaxLengthRule('name', nameLabel, 200));
  rules.push(createRequiredRule('brand', brandLabel));

  // Organization fields (required when enabled by family config)
  if (family.visualizationConfig?.showProductType) {
    rules.push(createRequiredRule('organization.productType', productTypeLabel));
  }
  if (family.visualizationConfig?.showApplicationTypes) {
    rules.push(createRequiredRule('organization.applicationTypes', applicationTypesLabel));
  }

  // Family-specific attribute rules (only for fields stored in attributes object)
  const requiredAttributes = getRequiredAttributes(family);
  for (const attr of requiredAttributes) {
    // Skip fields that have dedicated UI (not stored in attributes object)
    if (SPECIAL_ATTRIBUTE_FIELDS.includes(attr.fieldName as any)) {
      continue;
    }
    rules.push(createRequiredRule(`attributes.${attr.fieldName}`, () => resolveTranslation(attr.fieldLabel, i18n.language)));

    // Add validation based on field type
    if (attr.validation) {
      if (attr.validation.min !== undefined || attr.validation.max !== undefined) {
        rules.push(
          createRangeRule(
            `attributes.${attr.fieldName}`,
            () => resolveTranslation(attr.fieldLabel, i18n.language),
            attr.validation.min,
            attr.validation.max
          )
        );
      }
      if (attr.validation.pattern) {
        rules.push(
          createPatternRule(
            `attributes.${attr.fieldName}`,
            () => resolveTranslation(attr.fieldLabel, i18n.language),
            attr.validation.pattern,
            attr.validation.message
          )
        );
      }
    }

    // URL validation for URL field types
    if (attr.fieldType === 'url') {
      rules.push(createUrlRule(`attributes.${attr.fieldName}`, () => resolveTranslation(attr.fieldLabel, i18n.language)));
    }
  }

  // Variant axis rules
  const enabledAxes = getEnabledVariantAxes(family);
  for (const axis of enabledAxes) {
    if (axis.required) {
      // At least one value must be provided for required axes
      rules.push({
        id: `axis:${axis.name}:required`,
        field: `_axisValues.${axis.name}`,
        validate: (_, context) => {
          // This will be checked separately via variants
          return true;
        },
        message: () => tv('AXIS_REQUIRED', { label: resolveTranslation(axis.label, i18n.language) }),
        severity: 'error',
        enabled: true,
      });
    }
  }

  return rules;
}

/**
 * Generate validation rules for variants
 */
function generateVariantRules(family: ProductFamily, uploadedPlankImages?: { [plankGroupId: string]: File[] }): ValidationRule[] {
  const rules: ValidationRule[] = [];

  // At least one variant must be selected
  rules.push({
    id: 'variants:atLeastOne',
    field: 'variants',
    validate: (value) => {
      if (!Array.isArray(value)) return false;
      return value.some((v: VariantV3) => v.isSelected);
    },
    message: () => tv('VARIANTS_AT_LEAST_ONE'),
    severity: 'error',
    enabled: true,
  });

  // Exactly one default variant
  rules.push({
    id: 'variants:oneDefault',
    field: 'variants',
    validate: (value) => {
      if (!Array.isArray(value)) return true;
      const selectedVariants = value.filter((v: VariantV3) => v.isSelected);
      if (selectedVariants.length === 0) return true; // Handled by atLeastOne rule
      const defaultCount = selectedVariants.filter((v: VariantV3) => v.isDefault).length;
      return defaultCount === 1;
    },
    message: () => tv('VARIANTS_ONE_DEFAULT'),
    severity: 'error',
    enabled: true,
  });

  // All selected variants must have SKU
  rules.push({
    id: 'variants:skuRequired',
    field: 'variants',
    validate: (value) => {
      if (!Array.isArray(value)) return true;
      const selectedVariants = value.filter((v: VariantV3) => v.isSelected);
      return selectedVariants.every((v: VariantV3) => v.sku && v.sku.trim().length > 0);
    },
    message: () => tv('VARIANTS_SKU_REQUIRED'),
    severity: 'error',
    enabled: true,
  });

  // Validate commerce link URLs on selected variants
  rules.push({
    id: 'variants:commerce:link:url',
    field: 'variants',
    validate: (value) => {
      if (!Array.isArray(value)) return true;
      const selectedVariants = value.filter((v: VariantV3) => v.isSelected);
      return selectedVariants.every((v: VariantV3) => {
        const link = v.commerce?.link;
        if (!link || typeof link !== 'string' || link.trim() === '') return true;
        try { new URL(link); return true; } catch { return false; }
      });
    },
    message: () => tv('VARIANTS_LINK_URL'),
    severity: 'error',
    enabled: true,
  });

  rules.push({
    id: 'variants:commerce:addToCartLink:url',
    field: 'variants',
    validate: (value) => {
      if (!Array.isArray(value)) return true;
      const selectedVariants = value.filter((v: VariantV3) => v.isSelected);
      return selectedVariants.every((v: VariantV3) => {
        const link = v.commerce?.addToCartLink;
        if (!link || typeof link !== 'string' || link.trim() === '') return true;
        try { new URL(link); return true; } catch { return false; }
      });
    },
    message: () => tv('VARIANTS_CART_URL'),
    severity: 'error',
    enabled: true,
  });

  // Surface products require visualization data
  if (family.visualizationConfig?.requiresSeamlessTexture) {
    rules.push({
      id: 'variants:visualization:surface',
      field: 'variants',
      validate: (value) => {
        if (!Array.isArray(value)) return true;
        const selectedVariants = value.filter((v: VariantV3) => v.isSelected);
        if (selectedVariants.length === 0) return true;

        for (const variant of selectedVariants) {
          // Family requires seamless texture → every selected variant must
          // actually be a surface variant (with matching visualization.type)
          // AND have material uploaded. Drafts from the integration sync
          // ship with a default/image_only visualization, so simply checking
          // `variant.visualization?.type === 'surface'` would skip them and
          // let publish through on an otherwise empty product.
          if (variant.visualization?.type !== 'surface') return false;
          const surface = variant.visualization.surface;
          // Must have either materialPath, materialPaths, or uploaded plank images
          // Single-mode uploads are keyed by variant.uuid, not plankGroupId
          const hasMaterial = surface?.materialPath
            || (surface?.materialPaths?.length ?? 0) > 0
            || (surface?.plankGroupId && (uploadedPlankImages?.[surface.plankGroupId]?.length ?? 0) > 0)
            || (surface?.isMultiple === false && (uploadedPlankImages?.[variant.uuid]?.length ?? 0) > 0);
          if (!hasMaterial) return false;
        }
        return true;
      },
      message: () => tv('VARIANTS_TEXTURE_REQUIRED'),
      severity: 'error',
      enabled: true,
    });

    // Cabochon octagons and squares are saved as one list the worker halves:
    // the same number of each, or the halves mix them up.
    rules.push({
      id: 'variants:visualization:surface:cabochonImages',
      field: 'variants',
      validate: (value) => {
        if (!Array.isArray(value)) return true;
        return value
          .filter((v: VariantV3) => v.isSelected && isCabochonPattern(v.axes?.pattern))
          .every((v: VariantV3) => {
            const plankGroupId = v.visualization?.surface?.plankGroupId || v.uuid;
            return !isUnbalancedCabochonList(uploadedPlankImages?.[plankGroupId]);
          });
      },
      message: () => tv('VARIANTS_CABOCHON_UNBALANCED'),
      severity: 'error',
      enabled: true,
    });

    // Opus takes one image per piece, or one for all four: two or three leave pieces guessed.
    rules.push({
      id: 'variants:visualization:surface:opusImages',
      field: 'variants',
      validate: (value) => {
        if (!Array.isArray(value)) return true;
        return value
          .filter((v: VariantV3) => v.isSelected && isOpusPattern(v.axes?.pattern))
          .every((v: VariantV3) => {
            const images = uploadedPlankImages?.[v.visualization?.surface?.plankGroupId || v.uuid];
            return !images?.length || isOpusImageCountValid(images.length);
          });
      },
      message: () => tv('VARIANTS_OPUS_INCOMPLETE'),
      severity: 'error',
      enabled: true,
    });

    // Single-mode surface variants require textureWidth and textureHeight
    rules.push({
      id: 'variants:visualization:surface:textureDimensions',
      field: 'variants',
      validate: (value) => {
        if (!Array.isArray(value)) return true;
        const selectedVariants = value.filter((v: VariantV3) => v.isSelected);

        for (const variant of selectedVariants) {
          if (variant.visualization?.type !== 'surface') continue;
          const surface = variant.visualization.surface;
          if (surface?.isMultiple !== false) continue;

          const w = surface.seamlessParams?.textureWidth;
          const h = surface.seamlessParams?.textureHeight;
          if (!w || w <= 0 || !h || h <= 0) return false;
        }
        return true;
      },
      message: () => tv('VARIANTS_TEXTURE_DIMENSIONS'),
      severity: 'error',
      enabled: true,
    });
  }

  // 3D model products require a model file
  if (family.visualizationConfig?.requires3DModel) {
    rules.push({
      id: 'variants:visualization:model3d',
      field: 'variants',
      validate: (value) => {
        if (!Array.isArray(value)) return true;
        const selectedVariants = value.filter((v: VariantV3) => v.isSelected);

        for (const variant of selectedVariants) {
          if (variant.visualization?.type === 'model3d') {
            const hasModel = !!variant.visualization.model3d?.modelPath;
            if (!hasModel) return false;
          }
        }
        return true;
      },
      message: () => tv('VARIANTS_MODEL_REQUIRED'),
      severity: 'error',
      enabled: true,
    });
  }

  return rules;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get a nested value from an object using dot notation path
 */
function getNestedValue(obj: any, path: string): unknown {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[part];
  }

  return current;
}

/**
 * Run a validation rule against a value
 */
function runRule(
  rule: ValidationRule,
  formState: ProductFormStateV3,
  family: ProductFamily,
  variants: VariantV3[]
): ValidationMessage | null {
  if (!rule.enabled) return null;

  // Get the value to validate
  let value: unknown;
  if (rule.field === 'variants') {
    value = variants;
  } else if (rule.field.startsWith('_axisValues.')) {
    // Handle axis values specially
    value = undefined;
  } else {
    value = getNestedValue(formState, rule.field);
  }

  // Create validation context
  const context: ValidationContext = {
    formState,
    family,
    field: rule.field,
    values: formState as unknown as Record<string, unknown>,
  };

  // Run validation
  const isValid = rule.validate(value, context);

  if (!isValid) {
    const message = typeof rule.message === 'function'
      ? rule.message(value, context)
      : rule.message;

    return {
      field: rule.field,
      message,
      severity: rule.severity,
      rule: rule.id,
    };
  }

  return null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProductValidationV3(
  options: UseProductValidationV3Options
): UseProductValidationV3Return {
  const { product, family, variants = [], uploadedPlankImages, realTimeValidation = true } = options;

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  const [result, setResult] = useState<ValidationResultV3>(createEmptyValidationResult());

  // ─────────────────────────────────────────────────────────────────────────
  // Generate Rules from Family
  // ─────────────────────────────────────────────────────────────────────────

  const rules = useMemo(() => {
    // Handle null family gracefully
    if (!family) return [];
    const formRules = generateRulesFromFamily(family);
    const variantRules = generateVariantRules(family, uploadedPlankImages);
    return [...formRules, ...variantRules];
  }, [family, uploadedPlankImages]);

  // ─────────────────────────────────────────────────────────────────────────
  // Validation Functions
  // ─────────────────────────────────────────────────────────────────────────

  const validate = useCallback((): ValidationResultV3 => {
    if (!product || !family) {
      return createEmptyValidationResult();
    }

    const messages: ValidationMessage[] = [];

    // Run all rules
    for (const rule of rules) {
      const validationMessage = runRule(rule, product, family, variants);
      if (validationMessage) {
        messages.push(validationMessage);
      }
    }

    // Build result
    const errorsByField: Record<FieldPath, string[]> = {};
    const errorsByCard: Record<string, string[]> = {};
    let errorCount = 0;
    let warningCount = 0;

    for (const msg of messages) {
      // Group by field
      if (!errorsByField[msg.field]) {
        errorsByField[msg.field] = [];
      }
      errorsByField[msg.field].push(msg.message);

      // Group by card/section
      const cardId = msg.field.split('.')[0];
      if (!errorsByCard[cardId]) {
        errorsByCard[cardId] = [];
      }
      errorsByCard[cardId].push(msg.message);

      // Count by severity
      if (msg.severity === 'error') {
        errorCount++;
      } else if (msg.severity === 'warning') {
        warningCount++;
      }
    }

    const validationResult: ValidationResultV3 = {
      isValid: errorCount === 0,
      messages,
      errorsByField,
      errorsByCard,
      errorCount,
      warningCount,
    };

    setResult(validationResult);
    return validationResult;
  }, [product, family, variants, rules]);

  const validateField = useCallback(
    (field: FieldPath): ValidationMessage[] => {
      if (!product || !family) return [];

      const fieldMessages: ValidationMessage[] = [];

      // Run rules that apply to this field
      for (const rule of rules) {
        if (rule.field === field) {
          const validationMessage = runRule(rule, product, family, variants);
          if (validationMessage) {
            fieldMessages.push(validationMessage);
          }
        }
      }

      return fieldMessages;
    },
    [product, family, variants, rules]
  );

  const clearErrors = useCallback(() => {
    setResult(createEmptyValidationResult());
  }, []);

  const hasError = useCallback(
    (field: FieldPath): boolean => {
      return (result.errorsByField[field]?.length ?? 0) > 0;
    },
    [result]
  );

  const getFieldErrors = useCallback(
    (field: FieldPath): string[] => {
      return result.errorsByField[field] || [];
    },
    [result]
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Real-time Validation
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (realTimeValidation && product) {
      // Debounce validation to avoid excessive runs
      const timeoutId = setTimeout(() => {
        validate();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [product, variants, realTimeValidation, validate]);

  // ─────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────

  return {
    result,
    validate,
    validateField,
    clearErrors,
    hasError,
    getFieldErrors,
  };
}

export default useProductValidationV3;
